/**
 * ContentValidator - Validates parsed D&D content against current architecture
 *
 * Ensures that parsed content from IntelligentContentParser complies with:
 * - CanonicalSchemas.ts interface requirements
 * - Current combat system architecture
 * - ActionEconomyProcessor compatibility
 * - Discrete handler validation patterns
 *
 * @date_of_change 2025-09-18 - Initial implementation for content validation
 */

class ContentValidator {

  /**
   * Validate parsed content against current architecture standards
   * @param {object} parsedContent - The parsed content from IntelligentContentParser
   * @param {string} targetEntity - 'character' or 'monster'
   * @returns {object} - Validation result with success, errors, and warnings
   */
  static validateParsedContent(parsedContent, targetEntity = 'unknown') {
    const { type, data, confidence } = parsedContent;

    const result = {
      success: true,
      errors: [],
      warnings: [],
      suggestions: []
    };

    // Check confidence threshold
    if (confidence < 0.3) {
      result.warnings.push('Low confidence in content parsing - please review carefully');
    }

    // Validate by content type
    switch (type) {
      case 'spell':
        this.validateSpellData(data, result);
        break;
      case 'action':
        this.validateActionData(data, result, targetEntity);
        break;
      case 'item':
        this.validateEquipmentData(data, result);
        break;
      case 'feature':
        this.validateFeatureData(data, result);
        break;
      case 'ability':
      case 'abilities':
        this.validateAbilityData(data, result);
        break;
      case 'character':
        this.validateCharacterData(data, result);
        break;
      case 'monster':
        this.validateMonsterData(data, result);
        break;
      default:
        result.errors.push(`Unknown content type: ${type}`);
        result.success = false;
    }

    return result;
  }

  /**
   * Validate spell data against FullSpellData interface
   */
  static validateSpellData(data, result) {
    // Required fields from CanonicalSchemas.ts FullSpellData
    const requiredFields = ['name', 'level', 'school', 'castingTime', 'range', 'components', 'duration', 'description'];

    for (const field of requiredFields) {
      if (!data[field]) {
        result.errors.push(`Spell missing required field: ${field}`);
        result.success = false;
      }
    }

    // Validate level
    if (typeof data.level === 'number' && (data.level < 0 || data.level > 9)) {
      result.errors.push('Spell level must be between 0 (cantrip) and 9');
      result.success = false;
    }

    // Validate school
    const validSchools = ['abjuration', 'conjuration', 'divination', 'enchantment', 'evocation', 'illusion', 'necromancy', 'transmutation', 'universal'];
    if (data.school && !validSchools.includes(data.school.toLowerCase())) {
      result.warnings.push(`Unknown spell school: ${data.school}. Consider using one of: ${validSchools.join(', ')}`);
    }

    // Validate damage structure if present
    if (data.damage) {
      if (!Array.isArray(data.damage)) {
        result.errors.push('Spell damage must be an array of {dice, type} objects');
        result.success = false;
      } else {
        data.damage.forEach((dmg, index) => {
          if (!dmg.dice || !dmg.type) {
            result.errors.push(`Spell damage entry ${index + 1} missing dice or type`);
            result.success = false;
          }
        });
      }
    }

    // Validate save structure if present
    if (data.save) {
      const validAbilities = ['strength', 'dexterity', 'constitution', 'intelligence', 'wisdom', 'charisma'];
      if (!validAbilities.includes(data.save.ability)) {
        result.errors.push(`Invalid saving throw ability: ${data.save.ability}`);
        result.success = false;
      }
    }

    // Validate spells if present (canonical)
    if (data.spells) {
      const spells = data.spells;
      if (spells.byLevel) {
        if (typeof spells.byLevel !== 'object') {
          result.errors.push('spells.byLevel must be an object keyed by levels 0-9');
          result.success = false;
        } else {
          for (const [lvl, arr] of Object.entries(spells.byLevel)) {
            const n = parseInt(lvl, 10);
            if (Number.isNaN(n) || n < 0 || n > 9) {
              result.errors.push(`spells.byLevel has invalid level key: ${lvl}`);
              result.success = false;
              continue;
            }
            if (!Array.isArray(arr)) {
              result.errors.push(`spells.byLevel[${lvl}] must be an array`);
              result.success = false;
            }
          }
        }
      }
      if (spells.prepared && !Array.isArray(spells.prepared)) {
        result.errors.push('spells.prepared must be an array');
        result.success = false;
      }
      if (spells.atWill && !Array.isArray(spells.atWill)) {
        result.errors.push('spells.atWill must be an array');
        result.success = false;
      }
      if (spells.slots) {
        if (typeof spells.slots !== 'object') {
          result.errors.push('spells.slots must be an object keyed by levels 1-9 with { total, used }');
          result.success = false;
        } else {
          for (const [lvl, slot] of Object.entries(spells.slots)) {
            const n = parseInt(lvl, 10);
            if (Number.isNaN(n) || n < 1 || n > 9) {
              result.errors.push(`spells.slots has invalid level key: ${lvl}`);
              result.success = false;
              continue;
            }
            if (typeof slot !== 'object' || slot === null) {
              result.errors.push(`spells.slots[${lvl}] must be an object { total, used }`);
              result.success = false;
              continue;
            }
            const { total, used } = slot;
            if (typeof total !== 'number' || total < 0) {
              result.errors.push(`spells.slots[${lvl}].total must be a non-negative number`);
              result.success = false;
            }
            if (typeof used !== 'number' || used < 0 || used > total) {
              result.errors.push(`spells.slots[${lvl}].used must be between 0 and total`);
              result.success = false;
            }
          }
        }
      }
    }

    // Validate equipment/inventory structure if present
    if (data.equipment) {
      const eq = data.equipment;
      if (eq.items && !Array.isArray(eq.items)) {
        result.errors.push('equipment.items must be an array');
        result.success = false;
      }
    }
  }

  /**
   * Validate action data against current combat architecture
   */
  static validateActionData(data, result, targetEntity) {
    // Required fields
    if (!data.name) {
      result.errors.push('Action missing required field: name');
      result.success = false;
    }

    // Validate attack structure (current monster schema)
    if (data.attack) {
      const attack = data.attack;

      // Validate attack type
      const validAttackTypes = ['meleeWeapon', 'rangedWeapon', 'meleeSpell', 'rangedSpell'];
      if (!validAttackTypes.includes(attack.type)) {
        result.errors.push(`Invalid attack type: ${attack.type}. Must be one of: ${validAttackTypes.join(', ')}`);
        result.success = false;
      }

      // Validate damage array
      if (attack.damage) {
        if (!Array.isArray(attack.damage)) {
          result.errors.push('Attack damage must be an array');
          result.success = false;
        } else {
          attack.damage.forEach((dmg, index) => {
            if (!dmg.dice) {
              result.errors.push(`Damage entry ${index + 1} missing dice field`);
              result.success = false;
            }
            if (!dmg.type) {
              result.errors.push(`Damage entry ${index + 1} missing type field`);
              result.success = false;
            }
          });
        }
      }

      // Validate reach for melee attacks
      if (attack.type.includes('melee') && !attack.reach) {
        result.warnings.push('Melee attack missing reach - defaulting to 5 feet');
        attack.reach = 5;
      }

      // Validate range for ranged attacks
      if (attack.type.includes('ranged') && !attack.range) {
        result.warnings.push('Ranged attack missing range - please specify normal/long range');
      }
    }

    // Validate for specific entity types
    if (targetEntity === 'character') {
      // Character actions may need additional validation
      if (data.actionType && !['action', 'bonus', 'reaction', 'free'].includes(data.actionType)) {
        result.warnings.push(`Unknown action type for character: ${data.actionType}`);
      }
    } else if (targetEntity === 'monster') {
      // Monster actions should follow current schema
      if (!data.attack && !data.description) {
        result.warnings.push('Monster action has no attack data or description');
      }
    }
  }

  /**
   * Validate equipment data against FullEquipmentData interface
   */
  static validateEquipmentData(data, result) {
    // Required fields
    if (!data.name) {
      result.errors.push('Equipment missing required field: name');
      result.success = false;
    }

    if (!data.type) {
      result.errors.push('Equipment missing required field: type');
      result.success = false;
    }

    // Validate type
    const validTypes = ['weapon', 'armor', 'item', 'consumable'];
    if (!validTypes.includes(data.type)) {
      result.errors.push(`Invalid equipment type: ${data.type}. Must be one of: ${validTypes.join(', ')}`);
      result.success = false;
    }

    // Validate rarity if present
    if (data.rarity) {
      const validRarities = ['common', 'uncommon', 'rare', 'very rare', 'legendary', 'artifact'];
      if (!validRarities.includes(data.rarity)) {
        result.errors.push(`Invalid rarity: ${data.rarity}. Must be one of: ${validRarities.join(', ')}`);
        result.success = false;
      }
    }

    // Validate weapon-specific fields
    if (data.type === 'weapon') {
      if (data.damage && !data.damageType) {
        result.warnings.push('Weapon has damage but no damage type specified');
      }

      if (data.properties && !Array.isArray(data.properties)) {
        result.errors.push('Weapon properties must be an array');
        result.success = false;
      }
    }

    // Validate armor-specific fields
    if (data.type === 'armor') {
      if (data.armorClass && typeof data.armorClass !== 'number') {
        result.errors.push('Armor AC must be a number');
        result.success = false;
      }
    }

    // Validate effects structure if present
    if (data.effects) {
      if (!Array.isArray(data.effects)) {
        result.errors.push('Equipment effects must be an array');
        result.success = false;
      } else {
        data.effects.forEach((effect, index) => {
          if (!effect.type || !effect.description) {
            result.errors.push(`Effect ${index + 1} missing type or description`);
            result.success = false;
          }
        });
      }
    }
  }

  /**
   * Validate feature data against FullFeatureData interface
   */
  static validateFeatureData(data, result) {
    // Required fields
    if (!data.name) {
      result.errors.push('Feature missing required field: name');
      result.success = false;
    }

    if (!data.description) {
      result.errors.push('Feature missing required field: description');
      result.success = false;
    }

    // Validate action type if present
    if (data.actionType) {
      const validActionTypes = ['action', 'bonus', 'reaction', 'passive'];
      if (!validActionTypes.includes(data.actionType)) {
        result.errors.push(`Invalid action type: ${data.actionType}. Must be one of: ${validActionTypes.join(', ')}`);
        result.success = false;
      }
    }

    // Validate uses structure if present
    if (data.uses) {
      if (typeof data.uses.max !== 'number' || data.uses.max < 0) {
        result.errors.push('Feature uses.max must be a positive number');
        result.success = false;
      }

      if (data.uses.reset) {
        const validResets = ['short', 'long', 'dawn', 'custom'];
        if (!validResets.includes(data.uses.reset)) {
          result.errors.push(`Invalid uses reset: ${data.uses.reset}. Must be one of: ${validResets.join(', ')}`);
          result.success = false;
        }
      }
    }
  }

  /**
   * Validate ability score data
   */
  static validateAbilityData(data, result) {
    const validAbilities = ['strength', 'dexterity', 'constitution', 'intelligence', 'wisdom', 'charisma'];

    for (const [ability, score] of Object.entries(data)) {
      if (!validAbilities.includes(ability)) {
        result.errors.push(`Invalid ability name: ${ability}. Must be one of: ${validAbilities.join(', ')}`);
        result.success = false;
      }

      if (typeof score !== 'number' || score < 1 || score > 30) {
        result.errors.push(`Invalid ability score for ${ability}: ${score}. Must be between 1 and 30`);
        result.success = false;
      }
    }
  }

  /**
   * Validate character stat block data
   */
  static validateCharacterData(data, result) {
    // date_of_change: 2026-02-25
    // rule_reference: SmartPaste imports must be actionable; do not reference internal docs in end-user errors.
    if (!data || typeof data !== 'object') {
      result.errors.push('Character data is missing or invalid (expected an object)');
      result.success = false;
      return;
    }

    if (!data.name) {
      result.errors.push('Character missing required field: name');
      result.success = false;
    }

    const missingCanonical = [];
    if (!data.abilities) missingCanonical.push('all six ability scores');
    if (!data.ac) missingCanonical.push('Armor Class');
    if (!(data.maxHp || data.hp)) missingCanonical.push('current and maximum hit points');
    if (missingCanonical.length > 0) {
      const guidance = 'For a full character import, include those details in the paste. Example formats: "STR 10 DEX 10 CON 10 INT 10 WIS 10 CHA 10", "AC 16", and "HP 45 / 45".';
      result.errors.push(`SmartPaste still needs ${missingCanonical.join(', ')} before it can reliably replace the whole character sheet. ${guidance}`);
      result.warnings.push(`SmartPaste still needs ${missingCanonical.join(', ')} before a full-sheet import will be reliable.`);
      result.success = false;
    }

    if (data.abilities) {
      this.validateAbilityData(data.abilities, result);
    }

    if (data.hp) {
      if (typeof data.hp === 'object') {
        if (typeof data.hp.average !== 'number' || data.hp.average < 1) {
          result.errors.push('Character HP average must be a positive number');
          result.success = false;
        }
      } else if (typeof data.hp !== 'number' || data.hp < 1) {
        result.errors.push('Character HP must be a positive number');
        result.success = false;
      }
    }

    if (data.maxHp !== undefined && data.maxHp !== null) {
      if (typeof data.maxHp !== 'number' || data.maxHp < 1) {
        result.errors.push('Character maxHp must be a positive number');
        result.success = false;
      }
    }

    if (data.ac) {
      if (typeof data.ac === 'object') {
        if (typeof data.ac.value !== 'number' || data.ac.value < 1) {
          result.errors.push('Character AC value must be a positive number');
          result.success = false;
        }
      } else if (typeof data.ac !== 'number' || data.ac < 1) {
        result.errors.push('Character AC must be a positive number');
        result.success = false;
      }
    }

    if (data.speed) {
      if (typeof data.speed === 'object') {
        if (typeof data.speed.walk !== 'number' || data.speed.walk < 0) {
          result.errors.push('Character walk speed must be a non-negative number');
          result.success = false;
        }
      } else if (typeof data.speed !== 'number' || data.speed < 0) {
        result.errors.push('Character speed must be a non-negative number');
        result.success = false;
      }
    }
  }

  /**
   * Validate monster stat block data
   */
  static validateMonsterData(data, result) {
    if (!data || typeof data !== 'object') {
      result.errors.push('Monster data is missing or invalid (expected an object)');
      result.success = false;
      return;
    }

    if (!data.name) {
      result.errors.push('Monster missing required field: name');
      result.success = false;
    }

    if (data.abilities) {
      this.validateAbilityData(data.abilities, result);
    }

    if (data.hp) {
      if (typeof data.hp === 'object') {
        if (typeof data.hp.average !== 'number' || data.hp.average < 1) {
          result.errors.push('Monster HP average must be a positive number');
          result.success = false;
        }
      } else if (typeof data.hp !== 'number' || data.hp < 1) {
        result.errors.push('Monster HP must be a positive number');
        result.success = false;
      }
    }

    if (data.ac) {
      if (typeof data.ac === 'object') {
        if (typeof data.ac.value !== 'number' || data.ac.value < 1) {
          result.errors.push('Monster AC value must be a positive number');
          result.success = false;
        }
      } else if (typeof data.ac !== 'number' || data.ac < 1) {
        result.errors.push('Monster AC must be a positive number');
        result.success = false;
      }
    }
  }

  /**
   * Get validation suggestions based on content type and target entity
   */
  static getValidationSuggestions(parsedContent, targetEntity) {
    const { type, data } = parsedContent;
    const suggestions = [];

    switch (type) {
      case 'spell':
        if (!data.components) {
          suggestions.push('Consider adding components (V, S, M)');
        }
        if (!data.duration) {
          suggestions.push('Consider specifying duration');
        }
        break;

      case 'action':
        if (!data.attack && targetEntity === 'monster') {
          suggestions.push('Consider adding attack details for combat integration');
        }
        break;

      case 'item':
        if (data.type === 'weapon' && !data.properties) {
          suggestions.push('Consider adding weapon properties');
        }
        if (data.rarity && !data.attunement) {
          suggestions.push('Consider whether this item requires attunement');
        }
        break;

      case 'feature':
        if (!data.actionType) {
          suggestions.push('Consider specifying action type (action, bonus, reaction, passive)');
        }
        break;

      case 'character':
        if (!data.abilities) {
          suggestions.push('Add all six ability scores, for example: "STR 10 DEX 10 CON 10 INT 10 WIS 10 CHA 10".');
        }
        if (!data.ac) {
          suggestions.push('Add Armor Class, for example: "AC 16".');
        }
        if (!(data.maxHp || data.hp)) {
          suggestions.push('Add current and maximum hit points, for example: "HP 45 / 45".');
        }
        break;

      default:
        break;
    }

    return suggestions;
  }
}

export default ContentValidator;
